import React, { Component } from "react";
import { toastService } from "../../../services/toastService";
import { PageLayout } from "../../../components/ui";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import ExtendedInputText from "../../../components/ExtendedInputText";
import { merchantVendorApiService } from "../../../services/api/merchants/MerchantVendorApiService";
import { MerchantVendorServiceResponseModel, UpdateMerchantVendorRequestModel } from "../../../models/MerchantVendor";
import { paginatorTemplate } from "../../Paginator";
import { SortOrderType } from "../../../models/SearchRequestModel";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { InputTextFilerTemplate } from "../../Operations/TransactionFilterTemplates";
import { FilterX } from "lucide-react";

interface VendorSettingProps {
    vendorId: number;
}

interface VendorSettingState {
    isLoading: boolean;
    vendorServices: MerchantVendorServiceResponseModel[];
    totalRecords: number;
    rows: number;
    editRowId: number | null;
    backupRowData: MerchantVendorServiceResponseModel | null;
    filters: DataTableFilterMeta | undefined;
}

export default class VendorSetting extends Component<VendorSettingProps, VendorSettingState> {
    initializeFilters = {
        code: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nameBG: { value: null, matchMode: FilterMatchMode.CONTAINS },
        nameEN: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };

    constructor(props: VendorSettingProps) {
        super(props);
        this.state = {
            isLoading: false,
            vendorServices: [],
            totalRecords: 0,
            rows: 10,
            editRowId: null,
            backupRowData: null,
            filters: this.initializeFilters,
        };
    }

    async componentDidMount() {
        await this.loadVendorServicesData();
    }

    loadVendorServicesData = async () => {
        this.setState({ isLoading: true });
        await this.loadVendorServices();
        this.setState({ isLoading: false });
    };

    loadVendorServices = async () => {
        const vendorServices = await merchantVendorApiService.getVendorServices(this.props.vendorId);
        if (!vendorServices.success) {
            toastService.error(vendorServices.message);
            this.setState({ isLoading: false });
            return;
        }
        this.setState({ vendorServices: vendorServices.data, totalRecords: vendorServices.data.length });
    };

    saveRow = async (newData: MerchantVendorServiceResponseModel) => {
        this.setState({ isLoading: true });
        const request: UpdateMerchantVendorRequestModel = {
            id: newData.id,
            nameBG: newData.nameBG!,
            nameEN: newData.nameEN!,
            logoFile: "",
        };
        const result = await merchantVendorApiService.updateVendorService(request);
        if (!result.success) {
            toastService.error(result.message);
            this.setState({ isLoading: false });
            return;
        }
        toastService.success(result.message);
        this.setState({ editRowId: null, backupRowData: null });
        await this.loadVendorServicesData();
    };

    editRow = (rowData: MerchantVendorServiceResponseModel) => {
        this.setState({
            editRowId: rowData.id,
            backupRowData: { ...rowData },
        });
    };

    cancelEdit = () => {
        const { editRowId, backupRowData, vendorServices } = this.state;

        if (editRowId && backupRowData) {
            this.setState({
                vendorServices: vendorServices.map((vendor) => (vendor.id === editRowId ? backupRowData : vendor)),
                editRowId: null,
                backupRowData: null,
            });
        }
    };

    renderEditableField = (field: keyof MerchantVendorServiceResponseModel, rowData: MerchantVendorServiceResponseModel) => {
        const value = rowData[field] as string;
        return this.state.editRowId === rowData.id ? (
            <ExtendedInputText type="text" name={field} value={value || ""} onChange={(value) => this.onInputChange(field, value, rowData)} />
        ) : (
            rowData[field]
        );
    };

    onInputChange = (fieldName: string, value: string, rowData: MerchantVendorServiceResponseModel) => {
        const updatedVendorServices = this.state.vendorServices.map((vendor) => {
            if (vendor.id === rowData.id) {
                return { ...vendor, [fieldName]: value };
            }
            return vendor;
        });
        this.setState({ vendorServices: updatedVendorServices });
    };

    renderActionButtons = (rowData: MerchantVendorServiceResponseModel) => {
        if (rowData.isDeleted) {
            return null;
        }

        if (this.state.editRowId === rowData.id) {
            return (
                <>
                    <Button text className="p-2" onClick={() => this.saveRow(rowData)} label="Save" />
                    <Button text className="p-2" onClick={this.cancelEdit} label="Cancel" />
                </>
            );
        }

        return (
            <Button text onClick={() => this.editRow(rowData)} label="Edit" />
        );
    };

    renderHeader = () => (
        <span className="p-input-icon-left" style={{ display: "flex", justifyContent: "space-between" }}>
            <Button
                type="button"
                icon={<FilterX size={16} />}
                label="Clear"
                outlined
                onClick={() => {
                    this.setState({ filters: this.initializeFilters });
                }}
            />
        </span>
    );

    render() {
        return (
            <PageLayout noTitle>
                <DataTable
                                header={this.renderHeader}
                                editMode="row"
                                dataKey="id"
                                sortField="id"
                                sortOrder={SortOrderType.Ascending}
                                paginator
                                paginatorTemplate={paginatorTemplate}
                                rows={this.state.rows}
                                totalRecords={this.state.totalRecords}
                                value={this.state.vendorServices}
                                loading={this.state.isLoading}
                                stripedRows={true}
                                removableSort
                                rowClassName={(rowData) => (rowData.isDeleted ? "table-secondary" : "")}
                                filters={this.state.filters}
                                onFilter={(e) => this.setState({ filters: e.filters })}
                                filterDisplay="menu"
                            >
                                <Column
                                    field="code"
                                    header="Code"
                                    sortable
                                    filter
                                    filterField="code"
                                    filterMatchMode="contains"
                                    showFilterMatchModes={false}
                                    filterElement={InputTextFilerTemplate}
                                />

                                <Column
                                    field="name"
                                    header="Name"
                                    sortable
                                    filter
                                    filterField="name"
                                    filterMatchMode="contains"
                                    showFilterMatchModes={false}
                                    filterElement={InputTextFilerTemplate}
                                />

                                <Column
                                    field="nameBG"
                                    header="Name BG"
                                    body={(rowData) => this.renderEditableField("nameBG", rowData)}
                                    sortable
                                    filter
                                    filterField="nameBG"
                                    filterMatchMode="contains"
                                    showFilterMatchModes={false}
                                    filterElement={InputTextFilerTemplate}
                                />

                                <Column
                                    field="nameEN"
                                    header="Name EN"
                                    body={(rowData) => this.renderEditableField("nameEN", rowData)}
                                    sortable
                                    filter
                                    filterField="nameEN"
                                    filterMatchMode="contains"
                                    showFilterMatchModes={false}
                                    filterElement={InputTextFilerTemplate}
                                />

                    <Column header="Actions" body={(rowData) => this.renderActionButtons(rowData)} />
                </DataTable>
            </PageLayout>
        );
    }
}
